import { ResearchId } from "adapters/insights-agent/state/AppState.ts";
import { HttpClient } from "../../http/HttpClient.ts";
import { Subscribable } from "../../Subscribable.ts";
import { ClaState, Message, MessageState } from "./ClaState.ts";
import { Draft } from "immer";
import { arrayBufferToBase64 } from "./arrayBufferToBase64.tsx";
import lodashMerge from "lodash.merge";
import { replaceLegacyCitationPlaceholder } from "./replaceLegacyCitationPlaceholder.ts";
import {
  ProductEvent,
  ProductEventTarget,
} from "../../product-events/ProductEventTarget.ts";
import { AuthService } from "adapters/auth/AuthService.ts";

export class ClaService extends Subscribable<ClaState> {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly productEvents: ProductEventTarget,
    private readonly auth?: AuthService,
  ) {
    super({
      chatBoxSending: false,
      chats: [],
    });
  }

  async signOut() {
    await this.auth?.logout();
    this.updateState((draft) => {
      draft.chats = [];
      draft.chatBoxSending = false;
    });
  }

  async sendMessage(i: {
    chatId: string;
    message: string;
    skipEventLogging?: boolean;
  }): Promise<string | void> {
    if (!i.skipEventLogging) {
      this.productEvents.dispatchEvent(
        new ProductEvent("followup_message_send"),
      );
    }
    let optimisticAgentMsgRemoved = false;
    this.updateState((draft) => {
      draft.chatBoxSending = true;
      const chat = draft.chats.find((c) => c.id === i.chatId)!;
      // Optimistically add the user's message
      const initialPrompt = {
        id: "optimistic-user-msg-" + Date.now(),
        content: i.message,
        sender: "user" as const,
        createTime: Date.now(),
        status: "optimistic" as const,
      };
      const agentMessage = {
        id: "optimistic-agent-msg",
        content: "",
        sender: "agent" as const,
        createTime: Date.now() + 1,
        status: "processing" as const,
        thoughts: "",
      };
      chat.messages.push(initialPrompt, agentMessage);
    });
    let agentMsgId: string;
    this.httpClient
      .streamWithEventSource(
        `/agent/chats/${i.chatId}/query`,
        {
          message: i.message,
        },
        (event) => {
          if (!(event.event === "message" || event.event === "thought")) return;

          if (event.data?.msg_id) agentMsgId = event.data.msg_id;

          this.updateState((draft) => {
            const chat = this.findChatById(draft, i.chatId)!;
            if (!optimisticAgentMsgRemoved) {
              optimisticAgentMsgRemoved = true;
              chat.messages = chat.messages.filter(
                (m) => m.id !== "optimistic-agent-msg",
              );
            }

            const message = this.findOrCreateMessageInChatState(
              chat,
              agentMsgId,
            );

            if (event.data?.add_state) {
              message.state ??= {};
              lodashMerge(message.state, event.data.add_state);
            }

            if (event.data?.agent_name) {
              message.agentName = event.data.agent_name;
            }

            if (event.event === "message" && event.data?.add_content) {
              message.content += event.data.add_content;
              message.content = replaceLegacyCitationPlaceholder(
                message.content,
              );
            }

            if (event.event === "thought" && event.data?.add_content) {
              message.thoughts =
                (message.thoughts ?? "") + event.data.add_content;
            }
          });
        },
        () => {
          // onComplete
          this.updateState((draft) => {
            const chat = this.findChatById(draft, i.chatId)!;
            const message = this.findOrCreateMessageInChatState(
              chat,
              agentMsgId,
            );
            message.status = "completed";
            draft.chatBoxSending = false;
          });
        },
        (err) => {
          // onError
          console.error("Stream error: ", err);
          this.productEvents.dispatchEvent(
            new ProductEvent("exception", {
              description: "Stream error during message sending",
              fatal: false,
            }),
          );
          this.updateState((draft) => {
            const chat = this.findChatById(draft, i.chatId)!;
            const message = this.findOrCreateMessageInChatState(
              chat,
              agentMsgId,
            );
            message.status = "failed";
            message.content =
              "Something went wrong while answering your question. Please try again later.";
            draft.chatBoxSending = false;
          });
          throw err; // Re-throw to propagate the error and avoid reconnection
        },
      )
      .catch((err) => {
        console.error("Fatal error. Won't retry: ", err);
        this.productEvents.dispatchEvent(
          new ProductEvent("exception", {
            description: "Fatal error during message sending",
            fatal: true,
          }),
        );
        this.fetchChatMessages(i.chatId);

        try {
          this.updateState((draft) => {
            const chat = this.findChatById(draft, i.chatId)!;
            const message = this.findOrCreateMessageInChatState(
              chat,
              agentMsgId,
            );
            message.status = "failed";
            draft.chatBoxSending = false;
          });
        } catch (updateErr) {
          console.error("Error updating state after fatal error: ", updateErr);
        }
      });
  }

  async resendLastQuery(i: { chatId: string }): Promise<string | void> {
    const chat = this.getState().chats.find((c) => c.id === i.chatId);

    if (!chat) {
      throw new Error("Chat not found.");
    }

    const lastUserMessage = this.getLastMessage(i.chatId, "user");

    if (!lastUserMessage) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "No previous user message found to resend.",
          fatal: false,
        }),
      );
      throw new Error("No previous user message found to resend.");
    }

    this.productEvents.dispatchEvent(new ProductEvent("message_retry"));

    return this.sendMessage({
      chatId: i.chatId,
      message: lastUserMessage.content,
    });
  }

  getLastMessage(
    chatId: string,
    sender?: Message["sender"],
  ): Message | undefined {
    const chat = this.getState().chats.find((c) => c.id === chatId);
    if (!chat || chat.messages.length === 0) {
      return undefined;
    }
    return chat.messages.findLast((msg) =>
      sender ? msg.sender === sender : true,
    );
  }

  /**
   * Finds a message in a chat by ID. If not found, creates a new message using the provided msgId.
   */
  private findOrCreateMessageInChatState(
    chat: Draft<ClaState["chats"]>[number],
    msgId: string,
  ) {
    let message = chat.messages.find((m) => m.id === msgId);
    if (!message) {
      message = {
        id: msgId,
        content: "",
        sender: "agent",
        createTime: Date.now() + 1,
        status: "processing",
        thoughts: "",
      };
      chat.messages.push(message);
    }
    return message;
  }

  private findChatById(state: ClaState | Draft<ClaState>, chatId: string) {
    return state.chats.find((c) => c.id === chatId);
  }

  async createChatSession({
    initial_message,
    agent = "DSA",
    engine = "agentengine",
    businessDomain = "InnovationConceptTesting",
    agentNameOverride,
  }: {
    initial_message: string;
    agent?: "DSA" | "DRA";
    engine?: "agentengine" | "cloudrun";
    businessDomain?: "InnovationProductTesting" | "InnovationConceptTesting";
    agentNameOverride?: string;
  }) {
    console.log("Creating new chat session...");
    if (agentNameOverride) {
      console.log(`[DEV ONLY] Using agent name override: ${agentNameOverride}`);
    }
    this.updateState((draft) => {
      draft.chatBoxSending = true;
    });
    const response = await this.httpClient.post<{ chat_id: string }>(
      `/agent/chat`,
      {
        initial_message,
      },
      {
        agent: agentNameOverride || this.determineAgentName(engine, agent),
        engine,
        business_domain: businessDomain,
      },
    );
    if (!response.success || !response.data) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to create chat session",
          fatal: false,
        }),
      );
      throw new Error("Failed to create chat session.");
    }
    this.productEvents.dispatchEvent(new ProductEvent("chat_start"));
    const chatId = response.data.chat_id;
    this.updateState((draft) => {
      draft.chats.unshift({
        id: chatId,
        name: initial_message.substring(0, 100),
        messages: [],
        createTime: Date.now(),
        updatedTime: Date.now(),
      });
    });
    return chatId;
  }

  /**
   * Fetches the raw content of an artifact by its ID within a specific chat.
   * Returns the base64 content as a string.
   */
  async fetchImageContent(chatId: string, artifactId: string): Promise<string> {
    const res = await this.httpClient.getRaw(
      `/agent/chats/${chatId}/artifacts/${artifactId}`,
    );
    if (!res.ok) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to fetch image content",
          fatal: false,
        }),
      );
      console.error("Failed to fetch image content");
      return "";
    }
    const arrayBuffer = await res.arrayBuffer();
    return arrayBufferToBase64(arrayBuffer);
  }

  async downloadCitationResource(chatId: string, resourceLocator: string) {
    this.productEvents.dispatchEvent(new ProductEvent("citation_follow"));
    try {
      const filename = resourceLocator.match(/[^/]+$/)?.[0];
      const blob = await this.httpClient
        .getRaw(`/agent/chats/${chatId}/citation`, {
          citation_resource: resourceLocator,
        })
        .then((result) => result.blob());
      const obj = URL.createObjectURL(blob);
      const anchorTag = document.createElement("a");
      anchorTag.href = obj;
      anchorTag.download = filename!;
      document.body.appendChild(anchorTag);
      anchorTag.click();
      document.body.removeChild(anchorTag);
      URL.revokeObjectURL(obj);
    } catch (error) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to download citation resource",
          fatal: false,
        }),
      );
      console.error("Failed to download citation resource:", error);
    }
  }

  /**
   * Determines the agent name based on the engine and agent type.
   */
  private determineAgentName(
    engine: "agentengine" | "cloudrun",
    agent: "DSA" | "DRA",
  ) {
    let agentName: "DRA" | "DSA" | "deep_research_agent" | "data_science_agent";
    if (engine === "agentengine") {
      agentName = agent;
    } else {
      agentName =
        agent === "DRA" ? "deep_research_agent" : "data_science_agent";
    }
    return agentName;
  }

  async deleteChat(i: { chatId: ResearchId }) {
    const response = await this.httpClient.delete(`/agent/chats/${i.chatId}`);
    if (!response.success) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to delete chat",
          fatal: false,
        }),
      );
      console.error("Failed to delete chat: HTTP", response.httpStatus);
      throw new Error("Failed to delete chat.");
    }
    this.updateState((draft) => {
      draft.chats = draft.chats.filter((chat) => chat.id !== i.chatId);
    });
  }

  async renameChat(i: { chatId: ResearchId; name: string }) {
    const response = await this.httpClient.patch(
      `/agent/chats/${i.chatId}/title`,
      {
        title: i.name,
      },
    );
    if (!response.success) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to rename chat",
          fatal: false,
        }),
      );
      console.error("Failed to rename chat: HTTP", response.httpStatus);
      return;
    }
    this.updateState((draft) => {
      const chat = draft.chats.find((chat) => chat.id === i.chatId)!;
      chat.name = i.name;
    });
  }

  async fetchChatHistory(options?: {
    agent?: string;
    businessDomain?: "InnovationProductTesting" | "InnovationConceptTesting";
  }) {
    const queryParams: Record<string, string> = {};
    if (options?.agent) {
      queryParams.agent = options.agent;
    }
    if (options?.businessDomain) {
      queryParams.business_domain = options.businessDomain;
    }

    const response = await this.httpClient.get<{
      chats: {
        id: string;
        title: string;
        created_at: string;
        updated_at: string;
      }[];
    }>("/agent/chats", queryParams);

    if (!response.success) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to fetch chat history",
          fatal: false,
        }),
      );
      console.error("Failed to fetch chat history: HTTP", response.httpStatus);
      return;
    }

    this.updateState((draft) => {
      if (!response.data) return;
      for (const chat of response.data.chats) {
        if (!draft.chats.find((c) => c.id === chat.id)) {
          draft.chats.push({
            id: chat.id,
            name: chat.title,
            messages: [],
            createTime: new Date(chat.created_at).getTime(),
            updatedTime: new Date(chat.updated_at).getTime(),
          });
        }
      }
      draft.chats.sort((a, b) => {
        return b.updatedTime - a.updatedTime;
      });
    });
  }

  async fetchChatMessages(chatId: string) {
    const response = await this.httpClient.get<{
      messages: {
        id: string;
        content: string;
        sender: "user" | "agent";
        created_at: string;
        state?: MessageState;
      }[];
    }>(`/agent/chats/${chatId}/messages`);

    if (!response.success) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "Failed to fetch messages for chat",
          fatal: false,
        }),
      );
      console.error(
        `Failed to fetch messages for chat ${chatId}: HTTP`,
        response.httpStatus,
      );
      return;
    }

    this.updateState((draft) => {
      const chat = draft.chats.find((c) => c.id === chatId);
      if (!chat) return;
      chat.messages =
        response.data?.messages.map(
          (message) =>
            ({
              id: message.id,
              content: message.content,
              sender: message.sender,
              createTime: new Date(message.created_at).getTime(),
              thoughts: chat.messages.find((m) => m.id === message.id)
                ?.thoughts,
              status: "completed",
              state: message.state,
            }) as Message,
        ) || [];
    });
  }
}
